/**
 * Session Management for UX Analytics
 *
 * Manages browser session IDs for tracking page views and user behavior.
 * Sessions expire after 30 minutes of inactivity.
 */

const SESSION_KEY = "verkh_ux_session_id";
const SESSION_ACTIVITY_KEY = "verkh_ux_session_activity";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Get or create a session ID for tracking
 * Sessions persist in sessionStorage and expire after 30 minutes of inactivity
 */
export function getSessionId(): string {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    const lastActivity = sessionStorage.getItem(SESSION_ACTIVITY_KEY);

    // Check if session is still valid
    if (stored && lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity, 10);
      if (elapsed < SESSION_TIMEOUT_MS) {
        // Update last activity time
        sessionStorage.setItem(SESSION_ACTIVITY_KEY, Date.now().toString());
        return stored;
      }
    }

    // Create new session
    const newSession = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, newSession);
    sessionStorage.setItem(SESSION_ACTIVITY_KEY, Date.now().toString());
    return newSession;
  } catch {
    // Fallback if sessionStorage is unavailable
    return crypto.randomUUID();
  }
}

/**
 * Update the session activity timestamp
 * Call this on user interactions to keep the session alive
 */
export function updateSessionActivity(): void {
  try {
    sessionStorage.setItem(SESSION_ACTIVITY_KEY, Date.now().toString());
  } catch {
    // Ignore errors
  }
}

/**
 * Check if we're in a valid browser environment for tracking
 */
export function canTrack(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof sessionStorage !== "undefined" &&
    typeof navigator !== "undefined"
  );
}
